import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPages.css';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-container">
      <div className="legal-content">
        <button className="legal-back-btn" onClick={() => navigate(-1)}>← Back</button>

        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-updated">Last Updated: May 21, 2026</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>Enhance Technology Solutions (FlexPrime) ("Company," "we," "us," or "our") operates the myOmni mobile application ("App"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the App. Please read this policy carefully. If you disagree with its terms, please discontinue use of the App.</p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>
          <p>We may collect the following types of information:</p>
          <p><strong>a. Information You Provide Directly</strong></p>
          <ul>
            <li>Account registration details: name, email address, and password.</li>
            <li>Profile information: job title, company name, phone number, social media links, and profile photo.</li>
            <li>Content you add to your digital business card or link-in-bio page.</li>
          </ul>
          <p><strong>b. Information Collected Automatically</strong></p>
          <ul>
            <li>Device information: device type, operating system, and unique device identifiers.</li>
            <li>Usage data: pages viewed, features used, clicks, and time spent in the App.</li>
            <li>Log data: IP address, browser type, and app crash reports.</li>
          </ul>
          <p><strong>c. Information from Third Parties</strong></p>
          <ul>
            <li>If you connect third-party accounts (e.g., social media), we may receive basic profile information from those platforms.</li>
            <li>Payment information is handled directly by Apple App Store or Google Play and is not stored by us.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Create, operate, and maintain your account and App features.</li>
            <li>Enable sharing of your digital business card and link-in-bio page.</li>
            <li>Provide analytics on how your card or links are being viewed or clicked.</li>
            <li>Send service-related communications, such as account confirmations or support messages.</li>
            <li>Improve the App's features, performance, and user experience.</li>
            <li>Comply with legal obligations and enforce our Terms and Conditions.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. How We Share Your Information</h2>
          <p>We do not sell your personal data. We may share your information in the following circumstances:</p>
          <ul>
            <li><strong>Service Providers:</strong> With trusted third-party vendors who assist us in operating the App (e.g., cloud hosting, analytics, customer support), subject to confidentiality obligations.</li>
            <li><strong>Public Profile:</strong> Your digital business card and link-in-bio content is publicly accessible to anyone you share the link with.</li>
            <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction.</li>
            <li><strong>Legal Requirements:</strong> When required by law, regulation, or valid legal process (e.g., court order, subpoena).</li>
            <li><strong>Safety:</strong> To protect the rights, property, or safety of myOmni, our users, or the public.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Data Retention</h2>
          <p>We retain your personal data for as long as your account is active or as needed to provide services. If you delete your account, we will delete or anonymize your personal data within a reasonable period, except where retention is required by law.</p>
        </section>

        <section className="legal-section">
          <h2>6. Your Rights and Choices</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you.</li>
            <li>Request correction of inaccurate or incomplete information.</li>
            <li>Request deletion of your personal data (subject to legal obligations).</li>
            <li>Withdraw consent where processing is based on consent.</li>
            <li>Object to or restrict certain types of data processing.</li>
          </ul>
          <p>To exercise any of these rights, please contact us at <a href="mailto:support@my-omni.link">support@my-omni.link</a>.</p>
        </section>

        <section className="legal-section">
          <h2>7. Children's Privacy</h2>
          <p>The App is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have inadvertently collected such information, we will take steps to delete it promptly.</p>
        </section>

        <section className="legal-section">
          <h2>8. Data Security</h2>
          <p>We implement commercially reasonable technical and organizational security measures to protect your information from unauthorized access, use, alteration, or disclosure. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
        </section>

        <section className="legal-section">
          <h2>9. Third-Party Links and Services</h2>
          <p>The App may contain links to third-party websites or services. We are not responsible for the privacy practices of those third parties. We encourage you to review the privacy policies of any third-party sites you visit.</p>
        </section>

        <section className="legal-section">
          <h2>10. International Data Transfers</h2>
          <p>Your information may be transferred to and processed in countries other than your own. By using the App, you consent to the transfer of your data to the United States and other countries where our service providers operate.</p>
        </section>

        <section className="legal-section">
          <h2>11. California Privacy Rights (CCPA)</h2>
          <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, the right to delete personal information, and the right to opt out of the sale of personal information. As stated above, we do not sell personal information. To submit a CCPA request, contact us at <a href="mailto:support@my-omni.link">support@my-omni.link</a>.</p>
        </section>

        <section className="legal-section">
          <h2>12. Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of material changes via in-app notice or email. Your continued use of the App after changes take effect constitutes acceptance of the updated policy. We encourage you to review this policy periodically.</p>
        </section>

        <section className="legal-section">
          <h2>13. Contact Us</h2>
          <p>If you have questions, concerns, or requests regarding this Privacy Policy, please contact us:</p>
          <p>
            <strong>myOmni / Enhance Technology Solutions (FlexPrime)</strong><br />
            9266 Nagle Ave, Morton Grove, IL 60053<br />
            Email: <a href="mailto:support@my-omni.link">support@my-omni.link</a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
